'use strict';

import PQueue from 'p-queue';
import proj4 from 'proj4';
import {
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Matrix4Tuple,
  MeshBasicMaterial,
  Object3D,
  Points,
  Raycaster,
  ShaderLib,
  ShaderMaterial,
  Vector2,
  Vector3,
} from 'three';

import { GcpModel } from '../../../../typings/api/skymap/rest/v0/.common';
import { GeodataImage } from '../../../../typings/api/skymap/rest/v0/geodata';
import roundPointsFragmentShader from '../../../static/glsl/points/round-points.frag';
import {
  createCameraGeometry,
  getPositionFromGeodataImageTransform,
  updateCameraGeometry,
} from '../../react/components/cloud-processing/gcp-page/geodata-camera-utils';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { WebApi } from '../../services/web-api';
import { isDefined } from '../../utils/variables';
import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import { heightLabelUtils } from '../elements/components/height-labels';
import { sfmViewerColors } from '../elements/design/colors';
import { GcpXmlParser } from '../gcp/gcp-lib/gcp-xml-parser';
import { getAttributePositionAtIndex } from '../geodata/point-clouds/cesium-point-cloud/points/buffer-attribute/position-buffer-attribute';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { renderOrders } from '../render-orders';
import {
  MouseButtonType,
  MouseInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { PickingSphere } from './picking-sphere';
import { Tool } from './tool';

type GeodataPhoto = {
  name: string;
  thumbnail: string;
  preview: string;
  photo: string;
};

export interface InspectionToolImage {
  id: string;
  name: string;
  url: string;
  previewUrl: string;
  worldPosition: Vector3;
  transform?: Matrix4Tuple;
}

export type InspectionToolGeodata = {
  geodataId: string;
  images: InspectionToolImage[];
  gcpPoints?: GcpModel[];
};

export default class InspectionTool extends Tool {
  cursor: PickingSphere;
  gcpXmlParser?: GcpXmlParser;
  loadedGeodata?: InspectionToolGeodata;
  gcpPoints?: Points;
  gcpLabels?: Object3D;
  imageGroup?: Group;

  constructor(inputHandler: InputHandler) {
    super('inspection', inputHandler);

    this.visible = false;
    this.cursor = new PickingSphere(new Color(0xffff00), 10, 'round');
    this.add(this.cursor);
  }

  getMeta(sweref: string): MetaDefinition {
    return {
      src: 'tool.inspection',
      parameters: undefined,
    };
  }

  enable() {
    this.visible = true;
    this.inputHandler.setCursor('crosshair');
  }

  disable() {
    this.visible = false;
    this.inputHandler.setCursor('');
    this.unloadGeodata();
  }

  unloadGeodata() {
    this.removeGcpPoints();
    this.removeImageGroup();
    this.loadedGeodata = undefined;
  }

  onMouseMove(e: MouseInputData) {
    const image = this.intersectImages(e.pointer.position.x, e.pointer.position.y);
    this.publishImageHover(e, image);
    return true;
  }

  publishImageHover(e: MouseInputData, imageInfo?: ReturnType<typeof this.intersectImages>) {
    const camera = cameraManager().getActiveCamera();
    if (!camera || !this.imageGroup || !imageInfo) {
      publish(SubscriptionTopic.OnInspectionToolImageHover, { data: undefined });
      return;
    }

    // Convert world position to client/screen coordinates
    const screenPosition = camera.getScreenPosition(imageInfo.worldPosition);

    const position = {
      x: screenPosition.x,
      y: screenPosition.y,
    };

    publish(SubscriptionTopic.OnInspectionToolImageHover, {
      data: {
        url: imageInfo.image.url,
        name: imageInfo.image.name.substring(imageInfo.image.name.indexOf('_') + 1),
        position,
      },
    });
  }

  onMouseUp(e: MouseInputData) {
    if (this.withinThreshold(e.pointer.client.x, e.pointer.client.y)) {
      if (e.button.type === MouseButtonType.RightButton) {
        this.inputHandler.setTool('select');
      } else {
        // Make sure cursor is refreshed before opening the image inspection dialog.
        const imageInfo = this.intersectImages(e.pointer.position.x, e.pointer.position.y);
        if (imageInfo) {
          this.publishImageHover(e, undefined);
          publish(SubscriptionTopic.OpenImageInspectionDialog, {
            url: imageInfo.image.url,
            name: imageInfo.image.name.substring(imageInfo.image.name.indexOf('_') + 1),
          });
        }
      }
    }

    return Promise.resolve(true);
  }

  /**
   * Will intersect the children ("cameras") in the tool and hilight the selected one.
   */
  intersectImages(
    x: number,
    y: number,
  ):
    | Required<{
        image: InspectionToolImage;
        worldPosition: Vector3;
      }>
    | undefined {
    const camera = cameraManager().getActiveCamera();

    if (!camera || !this.imageGroup) {
      return;
    }

    // Get the cameras Points object from the group
    const imagePoints = this.imageGroup.children.find((child) => child instanceof Points) as
      | Points
      | undefined;
    if (!imagePoints) {
      return;
    }

    const raycaster = new Raycaster();
    raycaster.setFromCamera(new Vector2(x, y), camera.getTHREECamera());

    // Set threshold for point intersection
    raycaster.params.Points = { threshold: 5 };

    const intersections = raycaster.intersectObject(imagePoints);

    if (intersections.length > 0) {
      // Calculate distanceToRay for each intersection using actual point positions
      const tmpVec = new Vector3();
      const tmpVec2 = new Vector3();

      for (const intersection of intersections) {
        if (isDefined(intersection.index)) {
          // Get the actual position from the buffer attribute
          tmpVec.set(0, 0, 0);
          const localPosition = getAttributePositionAtIndex(
            imagePoints,
            intersection.index,
            tmpVec,
          );

          // Convert to world position
          tmpVec2.copy(localPosition);
          const actualPosition = imagePoints.localToWorld(tmpVec2);

          // Calculate distance from ray to actual point position
          intersection.distanceToRay = raycaster.ray.distanceToPoint(actualPosition);
        }
      }

      // Sort intersections by distanceToRay (closest first)
      intersections.sort((a, b) => {
        const distA = a.distanceToRay ?? Infinity;
        const distB = b.distanceToRay ?? Infinity;
        return distA - distB;
      });

      // Get the closest intersection
      const intersection = intersections[0];

      if (isDefined(intersection.index)) {
        // Get the actual position from the buffer attribute
        const localPosition = getAttributePositionAtIndex(imagePoints, intersection.index);

        // Convert to world position
        const worldPosition = imagePoints.localToWorld(localPosition);

        // Update cursor position
        this.cursor.position.copy(worldPosition);
        this.cursor.visible = true;

        const image = this.loadedGeodata?.images[intersection.index];

        if (image) {
          return { image, worldPosition };
        }
      }
    } else {
      // Hide cursor if no intersection
      this.cursor.visible = false;
    }
  }

  async getImages(geodataId: string, photoCount: number): Promise<InspectionToolImage[]> {
    const pQueue = new PQueue({ concurrency: 3 });
    const images: GeodataImage[] = [];
    const pageLimit = 2000;
    const chunksCount = Math.ceil(photoCount / pageLimit);

    for (let i = 0; i < chunksCount; i++) {
      void pQueue.add(async () => {
        const result = await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().getImages({
          path: { geodataId },
          query: {
            include: ['geodataImage.url', 'geodataImage.previewUrl'],
            limit: pageLimit,
            offset: i * pageLimit,
          },
        });
        images.push(...result.data);
      });
    }

    await pQueue.onIdle();

    // Get origo from CoordinateManager
    const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();

    return images.map((image) => {
      const wgs84Position = getPositionFromGeodataImageTransform(image.transform);
      const position = CoordinateManager.getInstance().wgs84CoordsToProject(wgs84Position);
      const worldPosition = new Vector3(position.easting, position.northing, position.height).sub(
        origo,
      );

      return {
        id: image.id,
        name: image.name,
        url: image.url ?? '',
        previewUrl: image.previewUrl ?? '',
        worldPosition,
        transform: image.transform as Matrix4Tuple | undefined,
      };
    });
  }

  async loadGeodata(geodataId: string): Promise<InspectionToolGeodata> {
    if (this.loadedGeodata?.geodataId === geodataId) {
      return this.loadedGeodata;
    }

    // Remove previous data.
    this.unloadGeodata();

    const geodata = await SkyMapAxiosServiceFactory.instance
      .createGeodataServiceV0()
      .fetchGeodataById({
        path: { geodataId },
        query: {
          include: ['geodata.gcpCollection'],
        },
      });

    const geodataImages: InspectionToolImage[] = geodata.data.isLegacyProcess
      ? await this.getLegacyImages(geodataId)
      : await this.getImages(geodataId, geodata.data.photoCount);

    const gcpPoints = geodata.data.gcpCollection?.points ?? [];

    this.loadGcpPoints(gcpPoints);

    if (geodataImages.length > 0) {
      const imageGroup = this.createImagePointsGeometry(geodataImages);
      this.imageGroup = imageGroup;
      this.add(this.imageGroup);
    }

    this.loadedGeodata = {
      geodataId,
      images: geodataImages,
      gcpPoints,
    };

    return this.loadedGeodata;
  }

  /**
   *  Returns the loaded geodata photos.
   *  It photos are not loaded, load them, store and return them.
   */
  async getGeodataPhotos(
    geodataId: string,
  ): Promise<{ photos: GeodataPhoto[]; xmlData: string } | undefined> {
    const geodataResponse = await SkyMapAxiosServiceFactory.instance
      .createGeodataServiceV0()
      .fetchGeodataById({ path: { geodataId }, query: {} });

    const pQueue = new PQueue({ concurrency: 3 });

    const photos: GeodataPhoto[] = [];
    const photoLimit = 1000;
    const chunksCount = Math.ceil(geodataResponse.data.photoCount / photoLimit);

    let xmlData: string | undefined;
    void pQueue.add(async () => {
      const getXmlRes = await SkyMapAxiosServiceFactory.instance
        .createGeodataServiceV0()
        .fetchXMLURL({ path: { geodataId } });
      xmlData = await WebApi.fetchS3DataAsText(getXmlRes.url);
    });

    for (let i = 0; i < chunksCount; i++) {
      void pQueue.add(async () => {
        const result = await SkyMapAxiosServiceFactory.instance
          .createGeodataServiceV0()
          .fetchPhotos({
            path: { geodataId },
            query: {
              limit: photoLimit,
              offset: i * photoLimit,
            },
          });

        photos.push(...result.data);
      });
    }

    await pQueue.onIdle();

    if (!isDefined(xmlData)) {
      return;
    }

    return { photos, xmlData };
  }

  async getLegacyImages(geodataId: string): Promise<InspectionToolImage[]> {
    const result = await this.getGeodataPhotos(geodataId);

    if (!isDefined(result?.photos) || !isDefined(result?.xmlData)) {
      return [];
    }

    this.gcpXmlParser = new GcpXmlParser(result.xmlData);
    const cameras = this.gcpXmlParser.cameras;
    const chunkTransform = this.gcpXmlParser.chunkTransform;

    for (const data of result.photos) {
      const label = this.removeExtension(data.name);
      const camera = cameras.find((c) => c.label === label);

      if (!camera) {
        continue;
      }

      camera.thumbnailUrl = data.thumbnail;
      camera.previewUrl = data.preview;
      camera.photoUrl = data.photo;
    }

    // Get origo from CoordinateManager
    const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();

    // Transform cameras to InspectionToolImage format
    const ecefToProjectConverter = proj4(
      'wgs84m',
      CoordinateManager.getInstance().getProjectProjection(),
    );
    const cameraTransform16 = cameras[0].transform
      ?.split(',')
      .map((e: string) => Number(e)) as number[];

    let estimatedGeoidHeight = 0;
    if (cameraTransform16) {
      const ecefPosition = metashapeWorldCameraPosition({
        cameraTransform16: cameraTransform16,
        chunkRotation9: chunkTransform?.rotation9 ?? [],
        chunkTranslation3: chunkTransform?.translation3 ?? [],
        chunkScale: chunkTransform?.scale ?? 1,
        // If you discover your camera 4x4 is a view matrix, flip this to true:
        isWorldToCamera: true,
      });
      const projectedPosition = ecefToProjectConverter.forward([
        ecefPosition[0],
        ecefPosition[1],
        ecefPosition[2],
      ]);
      estimatedGeoidHeight = (projectedPosition[2] - cameras[0].position.height) / 2;
    }

    return cameras
      .filter((camera) => isDefined(camera.photoUrl))
      .map((camera, index) => {
        const worldPosition = new Vector3(
          camera.position.easting,
          camera.position.northing,
          camera.position.height + estimatedGeoidHeight,
        ).sub(origo);

        return {
          id: index + camera.label,
          name: `${Date.now() / 1000}_${camera.label}.JPG`,
          url: camera.photoUrl ?? '',
          previewUrl: camera.previewUrl ?? '',
          worldPosition,
        };
      });
  }

  removeExtension(filename: string) {
    return filename.replace(/\.[^/.]+$/, '');
  }

  private removeImageGroup() {
    if (isDefined(this.imageGroup)) {
      this.remove(this.imageGroup);
      this.imageGroup = undefined;
    }
  }

  private removeGcpPoints() {
    if (isDefined(this.gcpPoints)) {
      this.remove(this.gcpPoints);
      this.gcpPoints = undefined;
    }
    if (isDefined(this.gcpLabels)) {
      this.remove(this.gcpLabels);
      this.gcpLabels = undefined;
    }
  }

  private createImagePointsGeometry(images: InspectionToolImage[]): Group {
    const imageCount = images.length;
    const geometry = createCameraGeometry(imageCount);

    geometry.cameras.renderOrder = renderOrders.aligner.gcpPoints;
    geometry.arrows.renderOrder = renderOrders.aligner.gcpPoints;
    geometry.arrowheads.renderOrder = renderOrders.aligner.gcpPoints;

    // Update positions and colors for points
    updateCameraGeometry({
      images,
      geometry,
      getPointIndex: (_image, arrayIndex) => arrayIndex,
      getPosition: (image) => {
        const { x, y, z } = image.worldPosition;
        return { x, y, z };
      },
      getColor: () => sfmViewerColors.cameras.default,
      getTransform: (image) => image.transform,
      hasStructuralChange: true,
    });

    return geometry.group;
  }

  /**
   * Loads GCP points with troika labels and adds them to the scene
   */
  loadGcpPoints(gcpPoints: GcpModel[]) {
    if (!isDefined(gcpPoints) || gcpPoints.length === 0) {
      return;
    }

    // Get origo from CoordinateManager
    const origo = CoordinateManager.getInstance().getProjectionOrigo();
    if (!isDefined(origo)) {
      return;
    }

    // Create points geometry
    this.gcpPoints = this.createGcpPointsGeometry(gcpPoints, origo);
    this.add(this.gcpPoints);

    // Create labels
    this.gcpLabels = this.createGcpLabelsTroika(gcpPoints, origo);
    this.add(this.gcpLabels);
  }

  onProjectProjectionUpdated(): boolean {
    const origoDifference = CoordinateManager.getInstance().origoDifference;
    if (isDefined(this.imageGroup)) {
      this.imageGroup.position.add(origoDifference);
    }
    if (isDefined(this.gcpPoints)) {
      this.gcpPoints.position.add(origoDifference);
    }
    if (isDefined(this.gcpLabels)) {
      this.gcpLabels.position.add(origoDifference);
    }
    return true;
  }

  private createGcpPointsGeometry(gcpPoints: GcpModel[], origo: Vector3): Points {
    const position = gcpPoints.map((x) => new Vector3(...x.enu).sub(origo).toArray()).flat();

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(position, 3));
    geometry.computeBoundingSphere();

    const points = new Points(
      geometry,
      new ShaderMaterial({
        vertexShader: ShaderLib.points.vertexShader,
        fragmentShader: roundPointsFragmentShader,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        uniforms: {
          size: { value: 10 },
          scale: { value: 1 },
          color: { value: new Color(...sfmViewerColors.gcp.default) },
        },
      }),
    );
    points.renderOrder = renderOrders.aligner.gcpPoints;
    return points;
  }

  private createGcpLabelsTroika(gcpPoints: GcpModel[], origo: Vector3): Object3D {
    const labelsRoot = new Object3D();
    const textMaterial = new MeshBasicMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      side: DoubleSide,
    });

    for (const gcp of gcpPoints) {
      labelsRoot.add(heightLabelUtils.createGcpLabel(gcp, origo, textMaterial));
    }
    return labelsRoot;
  }
}

/**
 * Extract camera position from a 4x4 matrix (row-major).
 *
 * If your matrix is camera->local (most common in Metashape exports),
 * translation is simply the last column: (m03, m13, m23).
 *
 * If your matrix is local/world->camera (view matrix),
 * camera center is: C = -R^T * t
 */
function cameraPositionFrom4x4RowMajor(m16: number[], { isWorldToCamera = false } = {}): number[] {
  if (!Array.isArray(m16) || m16.length !== 16) {
    throw new Error('Expected 16 numbers for a 4x4 row-major matrix.');
  }

  if (!isWorldToCamera) {
    // camera->local: translation in last column
    return [m16[3], m16[7], m16[11]];
  }

  // world->camera: C = -R^T * t
  const t = [m16[3], m16[7], m16[11]];
  const R = [
    [m16[0], m16[1], m16[2]],
    [m16[4], m16[5], m16[6]],
    [m16[8], m16[9], m16[10]],
  ];

  // R^T * t
  const Rt_t = [
    R[0][0] * t[0] + R[1][0] * t[1] + R[2][0] * t[2],
    R[0][1] * t[0] + R[1][1] * t[1] + R[2][1] * t[2],
    R[0][2] * t[0] + R[1][2] * t[1] + R[2][2] * t[2],
  ];

  return [-Rt_t[0], -Rt_t[1], -Rt_t[2]];
}

/**
 * Apply Metashape chunk transform:
 * world = T + s * (R * local)
 *
 * rotation9 is 3x3 row-major: [r00 r01 r02 r10 r11 r12 r20 r21 r22]
 * translation3 is [tx ty tz]
 */
function applyChunkTransform(
  localPos3: number[],
  rotation9: number[],
  translation3: number[],
  scale: number,
) {
  if (localPos3.length !== 3) {
    throw new Error('localPos3 must be length 3.');
  }
  if (rotation9.length !== 9) {
    throw new Error('rotation9 must be length 9.');
  }
  if (translation3.length !== 3) {
    throw new Error('translation3 must be length 3.');
  }
  if (typeof scale !== 'number') {
    throw new Error('scale must be a number.');
  }

  const [x, y, z] = localPos3;

  const r00 = rotation9[0],
    r01 = rotation9[1],
    r02 = rotation9[2];
  const r10 = rotation9[3],
    r11 = rotation9[4],
    r12 = rotation9[5];
  const r20 = rotation9[6],
    r21 = rotation9[7],
    r22 = rotation9[8];

  const rx = r00 * x + r01 * y + r02 * z;
  const ry = r10 * x + r11 * y + r12 * z;
  const rz = r20 * x + r21 * y + r22 * z;

  return [translation3[0] + scale * rx, translation3[1] + scale * ry, translation3[2] + scale * rz];
}

/**
 * Convenience: compute world camera position directly from Metashape values.
 */
function metashapeWorldCameraPosition({
  cameraTransform16, // 16 numbers (row-major)
  chunkRotation9, // 9 numbers (row-major)
  chunkTranslation3, // 3 numbers
  chunkScale, // number
  isWorldToCamera = false,
}: {
  cameraTransform16: number[];
  chunkRotation9: number[];
  chunkTranslation3: number[];
  chunkScale: number;
  isWorldToCamera?: boolean;
}) {
  const localPos = cameraPositionFrom4x4RowMajor(cameraTransform16, { isWorldToCamera });
  return applyChunkTransform(localPos, chunkRotation9, chunkTranslation3, chunkScale);
}
